import { useCallback, useEffect, useRef, useState } from 'react'
import DateVO from '../../../../../../../../../../vo/DateVO'
import { CalendarDateVO } from '../../../../../../../../../../domain/value-object/CalendarDateVO'

type UseBarCreationDragParams = {
  timeScale: CalendarDateVO[]
  canCreate: boolean
  commitBarCreation: (startDate: string, endDate: string) => void
  chartContainerRef: React.RefObject<HTMLDivElement>
}

type PreviewBar = {
  leftPercent: number
  widthPercent: number
  startDate: CalendarDateVO
  endDate: CalendarDateVO
}

type CursorGuide = {
  leftPercent: number
  widthPercent: number
  date: CalendarDateVO
}

const INTERACTIVE_SELECTOR = '.gantt-drag-point-icon, .MuiSlider-root'

// 予定線の新規作成ドラッグはグリッド全体で同時に1つだけ。フックは行ごとのインスタンスなので、
// ドラッグ中に別の行へホバーしたときにその行がカーソルガイドを出さないよう、状態を共有する
let isCreatingBarSomewhere = false

const isInteractiveTarget = (e: MouseEvent): boolean =>
  !!(e.target as HTMLElement).closest(INTERACTIVE_SELECTOR)

const getDateIndex = (
  clientX: number,
  containerRect: DOMRect,
  timeScaleLength: number
): number => {
  if (timeScaleLength === 0) return -1
  const percentX = ((clientX - containerRect.left) / containerRect.width) * 100
  const unitWidth = 100 / timeScaleLength
  const index = Math.floor(percentX / unitWidth)
  return Math.max(0, Math.min(timeScaleLength - 1, index))
}

const computePreviewBar = (
  startIdx: number,
  endIdx: number,
  timeScale: CalendarDateVO[]
): PreviewBar | null => {
  const len = timeScale.length
  if (len === 0) return null
  const unitWidth = 100 / len
  const minIdx = Math.min(startIdx, endIdx)
  const maxIdx = Math.max(startIdx, endIdx)
  return {
    leftPercent: minIdx * unitWidth,
    widthPercent: (maxIdx - minIdx + 1) * unitWidth,
    startDate: timeScale[minIdx],
    endDate: timeScale[maxIdx],
  }
}

export const useBarCreationDrag = ({
  timeScale,
  canCreate,
  commitBarCreation,
  chartContainerRef,
}: UseBarCreationDragParams) => {
  const [previewBar, setPreviewBar] = useState<PreviewBar | null>(null)
  const [cursorGuide, setCursorGuide] = useState<CursorGuide | null>(null)
  const dragStateRef = useRef<number | null>(null)
  const cleanupRef = useRef<(() => void) | null>(null)

  const timeScaleRef = useRef(timeScale)
  timeScaleRef.current = timeScale
  const commitRef = useRef(commitBarCreation)
  commitRef.current = commitBarCreation

  useEffect(() => {
    return () => {
      cleanupRef.current?.()
    }
  }, [])

  const resetCellCursor = useCallback(() => {
    const cell = chartContainerRef.current?.closest(
      '[col-id]'
    ) as HTMLElement | null
    if (cell) cell.style.cursor = ''
  }, [chartContainerRef])

  useEffect(() => {
    if (!canCreate) {
      resetCellCursor()
      setCursorGuide(null)
    }
  }, [canCreate, resetCellCursor])

  const handleCellMouseDown = useCallback(
    (e: MouseEvent, containerRect: DOMRect) => {
      if (!canCreate || e.button !== 0) return
      if (isInteractiveTarget(e)) return

      const ts = timeScaleRef.current
      const idx = getDateIndex(e.clientX, containerRect, ts.length)
      if (idx < 0) return

      e.preventDefault()
      dragStateRef.current = idx
      isCreatingBarSomewhere = true
      setPreviewBar(computePreviewBar(idx, idx, ts))
      setCursorGuide(null)

      const savedCursor = document.body.style.cursor
      const savedUserSelect = document.body.style.userSelect
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'

      const onMove = (ev: MouseEvent) => {
        const container = chartContainerRef.current
        if (!container || dragStateRef.current === null) return
        const rect = container.getBoundingClientRect()
        const endIdx = getDateIndex(
          ev.clientX,
          rect,
          timeScaleRef.current.length
        )
        setPreviewBar(
          computePreviewBar(dragStateRef.current, endIdx, timeScaleRef.current)
        )
      }

      const cleanup = () => {
        document.removeEventListener('mousemove', onMove)
        document.removeEventListener('mouseup', onUp)
        cleanupRef.current = null
        isCreatingBarSomewhere = false
        document.body.style.cursor = savedCursor
        document.body.style.userSelect = savedUserSelect
      }

      const onUp = (ev: MouseEvent) => {
        cleanup()
        resetCellCursor()

        const startIdx = dragStateRef.current
        dragStateRef.current = null
        setPreviewBar(null)

        if (startIdx === null) return
        const container = chartContainerRef.current
        if (!container) return

        const rect = container.getBoundingClientRect()
        const endIdx = getDateIndex(
          ev.clientX,
          rect,
          timeScaleRef.current.length
        )

        const curTs = timeScaleRef.current
        const minIdx = Math.min(startIdx, endIdx)
        const maxIdx = Math.max(startIdx, endIdx)
        if (minIdx < 0 || maxIdx >= curTs.length) return
        // 誤クリックで予定日が入るのを防ぐため、2日以上ドラッグした場合のみ作成する
        if (minIdx === maxIdx) return

        let startCal = curTs[minIdx]
        let endCal = curTs[maxIdx]
        if (startCal.isHoliday()) {
          startCal = CalendarDateVO.nextWorkDay(curTs, startCal, 1) ?? startCal
        }
        if (endCal.isHoliday()) {
          endCal = CalendarDateVO.previousWorkDay(curTs, endCal, 1) ?? endCal
        }
        if (startCal.isAfter(endCal)) {
          endCal = startCal
        }

        commitRef.current(
          new DateVO(startCal.date).serialize(),
          new DateVO(endCal.date).serialize()
        )
      }

      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup', onUp)
      cleanupRef.current = cleanup
    },
    [canCreate, chartContainerRef, resetCellCursor]
  )

  const handleCellMouseMove = useCallback(
    (e: MouseEvent, containerRect: DOMRect) => {
      if (dragStateRef.current !== null) return
      if (isCreatingBarSomewhere || !canCreate || isInteractiveTarget(e)) {
        setCursorGuide(prev => (prev !== null ? null : prev))
        return
      }

      const len = timeScaleRef.current.length
      if (len === 0) return
      const percentX =
        ((e.clientX - containerRect.left) / containerRect.width) * 100
      const unitWidth = 100 / len
      const snapIndex = Math.max(
        0,
        Math.min(Math.floor(percentX / unitWidth), len - 1)
      )
      const leftPercent = snapIndex * unitWidth
      setCursorGuide(prev =>
        prev?.leftPercent === leftPercent && prev.widthPercent === unitWidth
          ? prev
          : {
              leftPercent,
              widthPercent: unitWidth,
              date: timeScaleRef.current[snapIndex],
            }
      )

      const cell = chartContainerRef.current?.closest(
        '[col-id]'
      ) as HTMLElement | null
      if (cell) cell.style.cursor = 'col-resize'
    },
    [canCreate, chartContainerRef]
  )

  const handleCellLeave = useCallback(() => {
    if (dragStateRef.current === null) {
      setCursorGuide(null)
      resetCellCursor()
    }
  }, [resetCellCursor])

  return {
    handleCellMouseDown,
    handleCellMouseMove,
    handleCellLeave,
    previewBar,
    cursorGuide,
  }
}
